import { cn } from "@super-resume-new/ui/lib/utils";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Landing page layout primitives.
 *
 * The page is built on a single vertical rail: every band spans the viewport
 * and draws a hairline at its top edge, while the inner rail draws the two
 * vertical hairlines that frame the content column.
 */

export function Band({
  dark = false,
  className,
  children,
  id,
}: {
  dark?: boolean;
  className?: string;
  children: ReactNode;
  /** Anchor target, so a band can be linked to directly (e.g. /#pricing). */
  id?: string;
}) {
  return (
    <section
      id={id}
      className={cn(
        id && "scroll-mt-16",
        "border-t border-border",
        dark && "border-white/10 bg-neutral-950 text-neutral-200",
        className,
      )}
    >
      {children}
    </section>
  );
}

export function Rail({
  dark = false,
  className,
  children,
}: {
  dark?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "mx-auto max-w-7xl border-x border-border",
        dark && "border-white/10",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Faint graph-paper wash used behind product mockups. */
export const GRID_PAPER =
  "[background-image:linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] [background-size:36px_36px]";

export function Eyebrow({
  icon: Icon,
  children,
  dark = false,
  className,
}: {
  icon?: LucideIcon;
  children: ReactNode;
  dark?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium tracking-tight",
        dark
          ? "border-white/15 bg-white/5 text-neutral-300"
          : "border-border bg-card text-muted-foreground",
        className,
      )}
    >
      {Icon ? <Icon className="size-3.5 text-violet-500" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/**
 * Two-tone display heading: muted body copy with the emphasised words carried
 * in `<strong>` (rendered at full contrast, not bolder).
 */
export function Display({
  as: Tag = "h2",
  dark = false,
  className,
  children,
}: {
  as?: "h1" | "h2";
  dark?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Tag
      className={cn(
        "text-3xl font-semibold leading-[1.15] tracking-tight sm:text-4xl lg:text-[2.75rem]",
        dark ? "text-neutral-500" : "text-muted-foreground",
        "[&_strong]:font-semibold",
        dark ? "[&_strong]:text-white" : "[&_strong]:text-foreground",
        className,
      )}
    >
      {children}
    </Tag>
  );
}

/** Bold lead-in followed by muted continuation — the caption under each cell. */
export function Caption({
  icon: Icon,
  title,
  children,
  dark = false,
  className,
}: {
  icon?: LucideIcon;
  title: string;
  children: ReactNode;
  dark?: boolean;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "text-[13px] leading-relaxed",
        dark ? "text-neutral-400" : "text-muted-foreground",
        className,
      )}
    >
      <span className={cn("font-semibold", dark ? "text-white" : "text-foreground")}>
        {Icon ? (
          <Icon
            className="mr-1.5 inline size-3.5 align-[-0.15em] text-violet-500"
            aria-hidden="true"
          />
        ) : null}
        {title}
      </span>{" "}
      {children}
    </p>
  );
}
